import React, { useContext, useEffect, useState, useMemo } from "react";
import { motion, useDragControls, useMotionValue } from "motion/react";
import { X, Layers } from "lucide-react";
import cadviewer from "cadviewer";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { useSpaceToolsStore } from "../SpaceTools/stores/spaceToolsStore";
import EventSystem from "../../events/EventSystem";

// Position styles mapping (relative to the Space Tools button)
const positionStyles: Record<string, string> = {
  "bottom-left": "absolute top-0 left-0 z-[9999]",
  "bottom-right": "absolute top-0 right-0 z-[9999]",
  "top-right": "absolute top-0 right-0 z-[9999]",
};

interface LayerItem {
  id: string;
  name: string;
  state: "On" | "Off";
  color?: string; // If available in cvjs_getLayerTable
}

const LayerListPanel: React.FC = () => {
  const { config } = useConfig();
  const { currentCanvasInstance, currentDrawingPath, currentDrawingVersion } = useContext(CadviewerContext);
  const { activePanel, closePanel, selectedLayers, toggleLayerSelection, clearSelectedLayers } = useSpaceToolsStore();

  const isOpen = activePanel === "layerList";

  const [layers, setLayers] = useState<LayerItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortAscending, setSortAscending] = useState(true);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [includeFrozenLayers, setIncludeFrozenLayers] = useState(true);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const dragControls = useDragControls();

  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Automatically turn off the filter if we have no layers selected
  useEffect(() => {
    if (selectedLayers.length === 0 && showOnlySelected) {
      setShowOnlySelected(false);
    }
  }, [selectedLayers.length, showOnlySelected]);

  // Fetch layers when panel opens
  useEffect(() => {
    if (isOpen) {
      try {
        const layerTable = cadviewer.cvjs_getLayerTable(currentCanvasInstance || "floorPlan");
        console.log("=== CADViewer Layer Table ===", layerTable);
        
        if (layerTable && layerTable.layers) {
          const parsedLayers = layerTable.layers.map((l: any) => {
            let currentState = "On";
            if (l.layerState === "Off" || l.status === "OFF" || l.status === "off") {
              currentState = "Off";
            }
            return {
              id: l.layer || l.name || l.layerName,
              name: l.layerName || l.layer || l.name,
              state: currentState,
              color: l.colorHex || l.color || "#ccc",
            };
          });
          setLayers(parsedLayers);
        }
      } catch (e) {
        console.error("Error fetching layer table:", e);
      }
    }
  }, [isOpen, currentCanvasInstance]);

  const handleToggleLayer = (layerId: string, currentState: "On" | "Off") => {
    const newState = currentState === "On" ? "Off" : "On";
    
    if (newState === "On") {
      cadviewer.cvjs_LayerOn(layerId);
    } else {
      cadviewer.cvjs_LayerOff(layerId);
    }

    setLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, state: newState } : l))
    );
  };

  const handleAllLayersOn = () => {
    layers.forEach((l) => {
      if (l.state === "Off") cadviewer.cvjs_LayerOn(l.id);
    });
    setLayers((prev) => prev.map((l) => ({ ...l, state: "On" })));
  };

  const handleAllLayersOff = () => {
    layers.forEach((l) => {
      if (l.state === "On") cadviewer.cvjs_LayerOff(l.id);
    });
    setLayers((prev) => prev.map((l) => ({ ...l, state: "Off" })));
  };

  const handleSwapLayers = () => {
    layers.forEach((l) => {
      if (l.state === "On") {
        cadviewer.cvjs_LayerOff(l.id);
      } else {
        cadviewer.cvjs_LayerOn(l.id);
      }
    });
    setLayers((prev) =>
      prev.map((l) => ({ ...l, state: l.state === "On" ? "Off" : "On" }))
    );
  };

  const filteredAndSortedLayers = useMemo(() => {
    let result = [...layers];
    
    if (showOnlySelected) {
      result = result.filter((layer) => selectedLayers.includes(layer.id));
    }
    
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((l) => l.name.toLowerCase().includes(lowerQuery));
    }

    result.sort((a, b) => {
      const compare = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      return sortAscending ? compare : -compare;
    });

    return result;
  }, [layers, searchQuery, sortAscending, showOnlySelected, selectedLayers]);

  const positionClass = positionStyles[config.spaceSelectionPosition] || positionStyles["bottom-left"];

  if (!isOpen) return null;

  return (
    <div className={positionClass}>
      <motion.div
        drag
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={dragConstraints}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
        className="bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden w-[450px] pointer-events-auto flex flex-col"
      >
        {/* Header */}
        <div
          className="px-3 py-2 text-white cursor-move flex items-center justify-between"
          style={{ background: 'linear-gradient(to right, #0f3557, #0a2a42)', userSelect: 'none' }}
          onPointerDown={(e) => dragControls.start(e)}
        >
          <h3 className="text-xs font-bold flex items-center gap-2">
            <Layers className="w-4 h-4" />
            CADViewer - Layer List
          </h3>
          <button
            onClick={closePanel}
            className="p-1 rounded transition-colors hover:bg-[#0a2a42]"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Loaded File Info */}
        <div 
          className="px-3 py-2 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5 items-center shrink-0"
          onClick={() => EventSystem.publish("panel.folder_list.open", {})}
          title="Click to open Folder Structure"
        >
          <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Loaded File:</span>
          <div className="overflow-x-auto whitespace-nowrap flex items-center h-full" style={{ scrollbarWidth: 'none' }}>
            <span className="text-[11px] font-medium text-slate-700">
              {(() => {
                if (!currentDrawingPath) return "No file loaded";
                let fileName = currentDrawingPath.split(/[/\\]/).pop() || "";
                try {
                  fileName = decodeURIComponent(fileName);
                } catch (e) {
                  // ignore
                }
                const ext = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
                if (ext) {
                  const firstExtIndex = fileName.toLowerCase().indexOf(ext.toLowerCase());
                  if (firstExtIndex !== -1) {
                    return fileName.substring(0, firstExtIndex + ext.length);
                  }
                }
                return fileName;
              })()}
            </span>
          </div>
          
          <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Version:</span>
          <div className="flex items-center">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
              currentDrawingVersion === 'Current' || currentDrawingVersion === 'LATEST_FROM_TREE' || !currentDrawingVersion 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-indigo-100 text-indigo-700'
            }`}>
              {currentDrawingVersion === 'LATEST_FROM_TREE' ? 'Latest' : (currentDrawingVersion || 'Current')}
            </span>
          </div>
        </div>

        {/* Search & Sort Controls */}
        <div className="p-3 border-b border-slate-200 flex flex-col gap-2">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              onClick={() => setSortAscending(!sortAscending)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700 whitespace-nowrap"
            >
              Sort by Layer {sortAscending ? "▲" : "▼"}
            </button>
          </div>
          
          {selectedLayers.length > 0 && (
            <div className="flex justify-between items-center px-1 pt-1 text-[11px]">
              <span className="text-indigo-600 font-semibold">{selectedLayers.length} layer{selectedLayers.length !== 1 ? 's' : ''} selected</span>
              <div className="flex gap-4 items-center">
                <button
                  onClick={() => {
                    const willShowOnlySelected = !showOnlySelected;
                    setShowOnlySelected(willShowOnlySelected);
                    if (willShowOnlySelected) {
                      setSearchQuery("");
                    }
                  }}
                  className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors cursor-pointer"
                >
                  {showOnlySelected ? "Show All Layers" : "Show Selected Layers"}
                </button>
                <button 
                  onClick={clearSelectedLayers} 
                  className="text-slate-500 hover:text-slate-800 underline transition-colors cursor-pointer"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Layer List */}
        <div className="overflow-y-auto" style={{ maxHeight: '350px' }}>
          {filteredAndSortedLayers.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">No layers found.</div>
          ) : (
            <div className="p-2 space-y-1">
              {filteredAndSortedLayers.map((layer) => {
                const isOn = layer.state === "On";
                return (
                  <div
                    key={layer.id}
                    className={`flex items-center gap-2.5 py-1 px-2 rounded-md ${
                      isOn ? "bg-amber-300/80" : "bg-slate-100"
                    } hover:brightness-95 transition-all`}
                  >
                    {/* Selection Checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedLayers.includes(layer.id)}
                      onChange={() => toggleLayerSelection(layer.id)}
                      className="w-3.5 h-3.5 flex-shrink-0 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                      title={`Select ${layer.name}`}
                    />

                    {/* Placeholder symbols for the hamburger/tag icons from screenshot */}
                    <span className="text-slate-500 opacity-60 flex-shrink-0 text-xs">☰</span>
                    <img src="/assets/cadviewer/app/images/singleIcons/svg/light-skin/layercolor.svg" alt="" className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
                    
                    {/* Layer toggle switch */}
                    <button
                      onClick={() => handleToggleLayer(layer.id, layer.state)}
                      className={`relative inline-flex flex-shrink-0 h-4 w-7 items-center rounded-full transition-colors duration-200 ease-in-out border border-transparent ${
                        isOn ? "bg-emerald-400 shadow-sm" : "bg-slate-300"
                      }`}
                      title={`Toggle ${layer.name}`}
                    >
                      <span className="sr-only">Toggle layer</span>
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out shadow-sm ${
                          isOn ? "translate-x-[14px]" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                    
                    <span className="text-[11px] font-semibold tracking-tight text-slate-800 truncate" title={layer.name}>
                      {layer.name}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Bottom Actions */}
        <div className="p-3 border-t border-slate-200 flex gap-2">
          <button onClick={handleAllLayersOn} className="flex-1 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 text-slate-700">
            All Layer On
          </button>
          <button onClick={handleAllLayersOff} className="flex-1 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 text-slate-700">
            All Layer Off
          </button>
          <button onClick={handleSwapLayers} className="flex-1 py-1.5 text-xs font-medium border border-slate-300 rounded hover:bg-slate-50 text-slate-700">
            Swap Layers
          </button>
        </div>

        {/* Additional Controls */}
        <div className="px-3 pb-3 flex items-center border-t border-slate-100 pt-2">
          {/* Frozen Layers Toggle */}
          <div className="flex items-center gap-2 pr-3 border-r border-slate-200">
            <span className="text-xs font-medium text-slate-700">Include Frozen Layers</span>
            <button
              onClick={() => setIncludeFrozenLayers(!includeFrozenLayers)}
              className={`relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                includeFrozenLayers ? "bg-emerald-400" : "bg-slate-200"
              }`}
              title="Toggle Frozen Layers"
            >
              <span className="sr-only">Toggle Frozen Layers</span>
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out shadow-sm ${
                  includeFrozenLayers ? "translate-x-[14px]" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
          
          {/* Space for future method */}
          <div className="flex items-center gap-2 pl-3">
            {/* Add future controls here */}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LayerListPanel;
